import { z } from "zod";
import type { RecordResult } from "./obligations.js";

/**
 * Import van keuringen uit Metrel ES Manager (docs/ARBEIDSMIDDELEN.md): een `.padfx`-project is een zip
 * met één XML-bestand (`DataSource.padf`). De server leest zip en XML; dit bestand zet de XML-boom om
 * naar arbeidsmiddelen met hun keuring, zodat de regels zonder database te testen zijn.
 */

export const METREL_SOURCE = "metrel";
export const METREL_MAX_APPLIANCES = 5000;

/** Eén XML-element, zoals de server het uit `DataSource.padf` leest. */
export interface XmlNode {
  tag: string;
  attrs: Record<string, string>;
  /** Tekst direct in dit element (getrimd), "" als er geen is. */
  text: string;
  children: XmlNode[];
}

const kids = (n: XmlNode | undefined, tag: string) => n?.children.filter((c) => c.tag === tag) ?? [];
const kid = (n: XmlNode | undefined, tag: string) => n?.children.find((c) => c.tag === tag);
const path = (n: XmlNode | undefined, ...tags: string[]) => tags.reduce<XmlNode | undefined>((x, t) => kid(x, t), n);
const textOf = (n: XmlNode | undefined, ...tags: string[]) => path(n, ...tags)?.text ?? "";
function* walk(n: XmlNode, tag: string): Generator<XmlNode> {
  for (const c of n.children) {
    if (c.tag === tag) yield c;
    yield* walk(c, tag);
  }
}

/** Eigenschappen van een structuurobject (`SO/Ps/P`) in ES Manager. */
const PROP = { applianceId: "194", name: "195", testedOn: "261", intervalMonths: "215", nextDueOn: "257" } as const;

/**
 * Metingen op MID-code. De namen staan niet in het bestand; afgeleid uit de omschrijving van de
 * testprocedures en de eenheden (MI 3340, ES Manager 1.28).
 */
export const METREL_MEASUREMENTS: Record<string, string> = {
  "80": "Beschermingsleiding",
  "118": "Isolatieweerstand",
  "147": "Polariteit",
  "287": "Vervangende lekstroom",
  "289": "Verschilstroom",
  "290": "Aanraakstroom",
};

export interface MetrelMeasurement {
  /** MID-code in ES Manager. */
  code: string;
  name: string;
  /** Hoofdresultaat met eenheid, zoals de meter het geeft (bijv. "0.18 Ohm", ">199.9 MOhm"). */
  value: string | null;
  /** Grenswaarde met eenheid; null als er geen is. */
  limit: string | null;
  /** Overige resultaten (bijv. testspanning, vermogen). */
  extra: string[];
  passed: boolean | null;
}

export interface MetrelCheck {
  item: string;
  passed: boolean | null;
}

export interface MetrelAppliance {
  /** Volgorde in het bestand (voor meldingen). */
  index: number;
  /** Appliance ID: de stickercode zoals de scanner hem invoerde. */
  applianceId: string;
  name: string;
  /** Pad in de structuur van ES Manager (bijv. de klant of locatie), zonder het apparaat zelf. */
  location: string[];
  /** Testprocedure (Auto Sequence), bijv. "Klasse II" of "NEN2484 ladders en trappen". */
  procedure: string | null;
  /** Uniek ID van deze test in ES Manager (voor opnieuw importeren zonder dubbele keuringen). */
  testId: string | null;
  testedOn: string | null;
  intervalMonths: number | null;
  nextDueOn: string | null;
  /** null = niet getest in dit bestand. */
  result: Extract<RecordResult, "goedgekeurd" | "afgekeurd"> | null;
  measurements: MetrelMeasurement[];
  checklist: MetrelCheck[];
}

export interface MetrelInstrument {
  model: string | null;
  name: string | null;
  serial: string | null;
  /** Kalibratiedatum (ISO). */
  calibratedOn: string | null;
  software: string | null;
}

export interface MetrelParsed {
  instrument: MetrelInstrument | null;
  /** Laatst opgeslagen in ES Manager (ISO-datum). */
  savedOn: string | null;
  appliances: MetrelAppliance[];
  problems: { index: number; message: string }[];
}

export const METREL_FORMAT_ERROR =
  "Dit is geen Metrel-project. Sla het project op in Metrel ES Manager en kies het .padfx-bestand.";

/** "13.03.2026" of "13.03.2026 11:00:47" → "2026-03-13"; null als het geen datum is. */
export function metrelDate(s: string | null | undefined): string | null {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec((s ?? "").trim());
  if (!m) return null;
  const [, d, mo, y] = m;
  const iso = `${y}-${mo!.padStart(2, "0")}-${d!.padStart(2, "0")}`;
  const t = Date.parse(`${iso}T00:00:00Z`);
  return Number.isNaN(t) || new Date(t).toISOString().slice(0, 10) !== iso ? null : iso;
}

/** Status in ES Manager: 1 = goed; een ander getal = fout; leeg = niet uitgevoerd. */
function passed(s: string): boolean | null {
  const v = s.trim();
  if (!v) return null;
  return v === "1";
}

const clean = (s: string) => s.replace(/\s+/g, " ").trim();

function measurement(m: XmlNode): MetrelMeasurement {
  const code = textOf(m, "MID");
  const params = new Map(kids(kid(m, "MPs"), "MP").map((p) => [p.attrs.Id ?? "", textOf(p, "V")]));
  const fn = params.get("161") || params.get("162") || "";
  const results = kids(kid(m, "Rs"), "R").map((r) => clean(textOf(r, "V")));
  const limits = kids(kid(m, "Ls"), "L")
    .map((l) => clean(textOf(l, "V")))
    .filter((v) => v && v.toLowerCase() !== "off");
  return {
    code,
    name: METREL_MEASUREMENTS[code] ?? `Meting ${fn || code}`,
    value: results[0] || null,
    limit: limits[0] ?? null,
    extra: results.slice(1).filter(Boolean),
    passed: passed(textOf(m, "S")),
  };
}

/** Visuele inspectie met namen (`CIT`): waarde 5 = goed (zo staat het bij elke goedgekeurde regel). */
function checklist(c: XmlNode): MetrelCheck[] {
  return kids(kid(c, "IBs"), "IB")
    .filter((ib) => textOf(ib, "PID") !== (c.attrs.Id ?? ""))
    .map((ib) => ({ item: clean(textOf(ib, "N")), passed: textOf(ib, "V") === "5" ? true : null }))
    .filter((x) => x.item);
}

/**
 * De XML-boom van `DataSource.padf` omzetten naar arbeidsmiddelen met hun laatste test. Geeft null
 * als dit geen ES Manager-database is.
 */
export function parseMetrel(root: XmlNode): MetrelParsed | null {
  if (root.tag !== "Database" || !kid(root, "Data")) return null;
  const i = kid(kid(root, "Is"), "I");
  const instrument: MetrelInstrument | null = i
    ? {
        model: textOf(i, "IMiNm") || null,
        name: textOf(i, "INm") || null,
        serial: textOf(i, "ISer") || null,
        calibratedOn: metrelDate(textOf(i, "ICalD")),
        software: textOf(i, "ISwV") || null,
      }
    : null;

  const objects = kids(kid(root, "Data"), "SO");
  const byId = new Map(objects.map((o) => [o.attrs.Id ?? "", o]));
  const locationOf = (o: XmlNode) => {
    const out: string[] = [];
    let pid = textOf(o, "PID");
    for (let guard = 0; pid && pid !== "-1" && guard < 20; guard++) {
      const p = byId.get(pid);
      if (!p) break;
      const n = clean(textOf(p, "N"));
      if (n) out.unshift(n);
      pid = textOf(p, "PID");
    }
    return out;
  };

  const appliances: MetrelAppliance[] = [];
  const problems: MetrelParsed["problems"] = [];
  objects.forEach((o, index) => {
    const props = new Map(kids(kid(o, "Ps"), "P").map((p) => [p.attrs.Id ?? "", textOf(p, "V")]));
    // Alleen apparaten hebben een Appliance ID; mappen (klant, locatie) niet.
    if (!props.has(PROP.applianceId)) return;
    const applianceId = clean(props.get(PROP.applianceId) ?? "");
    if (!applianceId) {
      problems.push({ index, message: "Apparaat zonder Appliance ID" });
      return;
    }
    const tests = kids(kid(o, "Ms"), "AT");
    // De laatste test in het bestand telt (ES Manager bewaart per project één reeks per apparaat).
    const at = tests[tests.length - 1];
    const interval = Number(props.get(PROP.intervalMonths));
    const result = at ? passed(textOf(at, "S")) : null;
    appliances.push({
      index,
      applianceId,
      name: clean(props.get(PROP.name) ?? "") || clean(textOf(o, "N")) || applianceId,
      location: locationOf(o),
      procedure: at ? clean(textOf(at, "AT_Header", "N")) || null : null,
      testId: at?.attrs.Id ?? null,
      testedOn: metrelDate(props.get(PROP.testedOn)) ?? (at ? metrelDate(firstDate(at)) : null),
      intervalMonths: Number.isInteger(interval) && interval > 0 && interval <= 120 ? interval : null,
      nextDueOn: metrelDate(props.get(PROP.nextDueOn)),
      result: result === null ? null : result ? "goedgekeurd" : "afgekeurd",
      measurements: at ? [...walk(at, "M")].map(measurement) : [],
      checklist: at ? [...walk(at, "CIT")].flatMap(checklist) : [],
    });
  });

  return { instrument, savedOn: metrelDate(textOf(root, "Header", "LastSavedDate")), appliances, problems };
}

/** Datum van de eerste meting of inspectie in een test (als de testdatum ontbreekt). */
function firstDate(at: XmlNode): string | null {
  for (const tag of ["M", "IT", "CIT"]) {
    for (const n of walk(at, tag)) {
      const dt = textOf(n, "DT") || kids(kid(n, "MPs"), "MP").find((p) => p.attrs.Id === "1")?.children[0]?.text;
      if (dt) return dt;
    }
  }
  return null;
}

/** Soort keuring bij een testprocedure: ladders en trappen (NEN 2484) of elektrisch (NEN 3140). */
export function metrelObligation(procedure: string | null): { key: string; name: string } {
  return /2484|ladder|trap/i.test(procedure ?? "")
    ? { key: "nen2484", name: "Keuring ladders en trappen (NEN 2484)" }
    : { key: "nen3140", name: "Keuring arbeidsmiddelen (NEN 3140)" };
}

/** Voorstel NL-SfB-code uit groep 8 (losse inrichting) op omschrijving en testprocedure. */
export function metrelElement(name: string, procedure: string | null): string {
  const s = `${name} ${procedure ?? ""}`.toLowerCase();
  if (/stofzuig|hogedruk|schrob|veegma|reinig/.test(s)) return "85.11";
  if (/vriez|koelkast|koelka|koffie|magnetron|waterkoker|vaatwas|oven/.test(s)) return "83.11";
  if (/ladder|trap|steiger|2484/.test(s)) return "82.13";
  if (/compressor|lader|werklamp|lamp|portofoon|radio|ventilator|kachel|accu/.test(s)) return "82.12";
  return "82.13";
}

const fileB64 = z
  .string()
  .min(1)
  .max(14 * 1024 * 1024);

export const metrelPlanBodySchema = z.object({ file: fileB64 });

export const metrelImportBodySchema = z.object({
  file: fileB64,
  fileName: z.string().trim().max(200).default("metrel.padfx"),
  /** Code per nieuw arbeidsmiddel (Appliance ID → NL-SfB-code; null = niet ingedeeld). */
  codes: z
    .array(z.object({ applianceId: z.string().max(200), code: z.string().max(12).nullable() }))
    .max(METREL_MAX_APPLIANCES)
    .default([]),
});
export type MetrelImportBody = z.input<typeof metrelImportBodySchema>;

/** new = arbeidsmiddel wordt aangemaakt; add = keuring komt bij een bestaand; done = al geïmporteerd. */
export type MetrelRowStatus = "new" | "add" | "done" | "untested" | "error";

export interface MetrelPlanRow {
  applianceId: string;
  name: string;
  location: string;
  procedure: string | null;
  testedOn: string | null;
  result: MetrelAppliance["result"];
  status: MetrelRowStatus;
  /** Bestaande asset (bij add/done). */
  asset: { id: string; code: string; name: string } | null;
  /** Voorgestelde code voor een nieuw arbeidsmiddel. */
  code: string | null;
  message: string | null;
}

export interface MetrelPlanResponse {
  instrument: MetrelInstrument | null;
  savedOn: string | null;
  rows: MetrelPlanRow[];
  problems: { index: number; message: string }[];
}

export interface MetrelImportResult {
  /** null als er niets te importeren was. */
  importId: string | null;
  assetsCreated: number;
  records: { created: number; skipped: number };
  rejected: number;
  errors: { applianceId: string; message: string }[];
}

/** Opgeslagen bij een keuring uit de meter (obligation_records.measurements). */
export interface MetrelRecordData {
  procedure: string | null;
  measurements: MetrelMeasurement[];
  checklist: MetrelCheck[];
}
